import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { HyreDocsIcon } from '../components/HyreDocsLogo';
import { Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';

export default function RegisterPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-[#F9F8F6]">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#1e293b] via-[#334155] to-[#1e293b] p-12 flex-col justify-between relative overflow-hidden">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center shadow-lg shadow-orange-500/30">
              <HyreDocsIcon className="w-6 h-6" />
            </div>
            <span className="font-heading font-bold text-2xl text-white">HyreDocs</span>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#f97316]/20 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-40 left-10 w-48 h-48 bg-gradient-to-br from-[#f59e0b]/15 to-transparent rounded-full blur-3xl" />
        
        <div className="space-y-6 relative z-10">
          <h2 className="font-heading text-4xl font-bold text-white leading-tight">
            Streamline your<br />candidate onboarding
          </h2>
          <p className="text-slate-300 font-body text-lg max-w-md leading-relaxed">
            Connect your Bullhorn instance and start sending documents to candidates in minutes.
          </p>
        </div>

        <div className="space-y-4 relative z-10">
          <div className="flex items-center gap-3 text-slate-300">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center">
              <span className="text-sm text-white">✓</span>
            </div>
            <span className="font-body">Direct Bullhorn integration</span>
          </div>
          <div className="flex items-center gap-3 text-slate-300">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center">
              <span className="text-sm text-white">✓</span>
            </div>
            <span className="font-body">E-signatures & document tracking</span>
          </div>
          <div className="flex items-center gap-3 text-slate-300">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center">
              <span className="text-sm text-white">✓</span>
            </div>
            <span className="font-body">Automatic data sync</span>
          </div>
        </div>
      </div>

      {/* Right side - Contact info */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center shadow-md">
              <HyreDocsIcon className="w-5 h-5" />
            </div>
            <span className="font-heading font-bold text-xl">HyreDocs</span>
          </div>

          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-foreground">Request Access</h1>
            <p className="text-muted-foreground font-body">
              HyreDocs is available to organizations by invitation only.
            </p>
          </div>

          <div className="neumorphic rounded-xl p-6 space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              If your organization would like to use HyreDocs, please contact us to set up your account.
            </p>
            
            <div className="space-y-3">
              <a 
                href="mailto:support@hyredocs.com?subject=HyreDocs Access Request"
                className="btn-primary w-full h-12 flex items-center justify-center gap-2"
              >
                Contact Us for Access
              </a>
            </div>
            
            <p className="text-xs text-center text-muted-foreground">
              Once your organization is set up, you'll receive login credentials from your administrator.
            </p>
          </div>

          <p className="text-center text-muted-foreground font-body">
            Already have an account?{' '}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
